/// <reference types="@types/google.maps" />
import { useEffect, useRef } from "react"
import { setOptions, importLibrary } from "@googlemaps/js-api-loader"

type AddressComponents = {
  streetAddress: string
  city: string
  state: string
  zipCode: string
  country: string
}

const API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY

if (API_KEY) {
  setOptions({ key: API_KEY })
}

export function useGooglePlacesAutocomplete(
  inputRef: React.RefObject<HTMLInputElement | null>,
  onSelect: (address: AddressComponents) => void,
  step: number,
) {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)

  useEffect(() => {
    if (!API_KEY) return

    let cancelled = false

    importLibrary("places")
      .then(({ Autocomplete }) => {
        if (cancelled || !inputRef.current) return

        autocompleteRef.current = new Autocomplete(inputRef.current, {
          types: ["address"],
          fields: ["address_components"],
        })

        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current?.getPlace()
          if (!place?.address_components) return

          const get = (type: string) =>
            place.address_components!.find((c) => c.types.includes(type))
              ?.long_name ?? ""

          const streetNumber = get("street_number")
          const route = get("route")

          onSelect({
            streetAddress: [streetNumber, route].filter(Boolean).join(" "),
            city: get("locality") || get("sublocality") || get("postal_town"),
            state: get("administrative_area_level_1"),
            zipCode: get("postal_code"),
            country: get("country"),
          })
        })
      })
      .catch(() => {
        // API unavailable — input remains fully functional for manual entry
      })

    return () => {
      cancelled = true
      if (autocompleteRef.current) {
        try {
          google.maps.event.clearInstanceListeners(autocompleteRef.current)
        } catch {
          // google may not be loaded if the API key was invalid
        }
        autocompleteRef.current = null
      }
    }
  }, [inputRef, onSelect, step])
}
